import { lazy, Suspense } from 'react';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import { CanvasState, ChatMode, ChatSource, Conversation, Exploration } from '@/explore/types';
import { CopiedCell } from '@/explore/utils/use-persist-cell';
import { ExplorationSearchModal } from '@/explore/exploration-search';
import { AnalyticsContextProvider } from '@/lib/analytics';
import { isEmptyExploration, setExplorationCanvas } from '@/core/exploration';

const InfiniteCanvas = lazy(() => import('../../components/canvas'));

import { CellInput } from '../cell-input';
import { ConversationInput } from '../conversation-cell/conversation-input';
import { CanvasCellInputTrigger } from '../cell-input/canvas-cell-input-trigger';

import canvasStyles from './exploration-canvas.module.scss';

type ExplorationCanvasProps = {
  exploration: Exploration;
  setExploration: (exploration: Exploration) => void;
  selectCell: (cellId: string) => void;
  deselectCell: () => void;
  showDataCatalog: boolean;
  setShowDataCatalog: (show: boolean) => void;
  searchTerm: string;
  setSearchTerm: (query: string) => void;
  newCellIndex: number | null;
  setNewCellIndex: (index: number | null) => void;
  newConversationSubjectId: string | null;
  setNewConversationSubjectId: (cellId: string | null) => void;
  onAsk: (
    searchQuery: string,
    sources: string[],
    mode: ChatMode,
    index: number,
  ) => Promise<{ conversation: Conversation | null; error: Error | undefined }>;
  onExplorationClick: (exploration: Exploration, index: number) => void;
  copiedCell: CopiedCell | undefined;
  onCellPaste: (cell: CopiedCell | undefined, index: number) => void;
  onBlockAdd: (index: number) => void;
};

export const ExplorationCanvas = (props: ExplorationCanvasProps) => {
  const {
    exploration,
    setExploration,
    selectCell,
    deselectCell,
    showDataCatalog,
    setShowDataCatalog,
    searchTerm,
    setSearchTerm,
    newCellIndex,
    setNewCellIndex,
    newConversationSubjectId,
    setNewConversationSubjectId,
    onAsk,
    onExplorationClick,
    copiedCell,
    onBlockAdd,
    onCellPaste,
  } = props;

  const persistCanvas = (state: CanvasState) => {
    if (!isEqual(state, exploration.view.canvas)) {
      setExploration(setExplorationCanvas(exploration, state));
    }
  };

  const isEmpty = isEmptyExploration(exploration);

  return (
    <Suspense fallback={<div className={canvasStyles.placeholder} />}>
      <InfiniteCanvas
        onChange={persistCanvas}
        onSelectionChange={(id) => {
          if (id !== undefined) {
            selectCell(id);
          } else {
            deselectCell();
          }
        }}
      />

      <div
        className={classNames([
          canvasStyles.canvasCta,
          newCellIndex === null &&
            newConversationSubjectId === null &&
            canvasStyles.canvasCtaBackground,
        ])}>
        {newCellIndex !== null ? (
          <CellInput
            onAsk={(search, sources, mode) => onAsk(search, sources, mode, 0)}
            onShowCatalog={(query) => {
              setSearchTerm(query);
              setShowDataCatalog(true);
            }}
            onClickExploration={(exploration) => onExplorationClick(exploration, 0)}
            onClose={() => setNewCellIndex(null)}
          />
        ) : newConversationSubjectId !== null ? (
          <ConversationInput
            floating
            sources={[ChatSource.Database]}
            onCancel={() => setNewConversationSubjectId(null)}
            onSubmit={(search, sources, mode) => onAsk(search, sources, mode, 0)}
          />
        ) : (
          <CanvasCellInputTrigger
            compact={false}
            showPaste={copiedCell !== undefined}
            onClick={() => onBlockAdd(0)}
            onPaste={() => onCellPaste(copiedCell, 0)}
          />
        )}
      </div>
      {showDataCatalog && newCellIndex !== null && (
        <AnalyticsContextProvider
          properties={{
            intent: 'new block',
            exploration,
            atIndex: 0,
          }}>
          <ExplorationSearchModal
            exploration={!isEmpty ? exploration : undefined}
            selectIndividualCells={!isEmpty}
            copiedCell={copiedCell}
            defaultSearchTerm={searchTerm}
            onClose={() => setShowDataCatalog(false)}
            onClickExploration={(exploration) => {
              setNewCellIndex(null);
              setShowDataCatalog(false);
              if (!isEmpty) {
                onExplorationClick(exploration, 0);
                return false;
              }
            }}
            onPasteExploration={() => copiedCell !== undefined && onCellPaste(copiedCell, 0)}
          />
        </AnalyticsContextProvider>
      )}
    </Suspense>
  );
};
